import { onScopeDispose } from 'vue';

// `overflow: hidden` on the body is not enough on iOS Safari: the page behind keeps scrolling.
// The body is pinned with `position: fixed` instead, and the scroll position restored on unlock.
// The lock is shared (reference counted) so that stacked modals do not release it too early.
const LOCKED_STYLES = ['position', 'top', 'left', 'right', 'width', 'overflow', 'overscrollBehavior'] as const;

let lockCount = 0;
let savedScrollX = 0;
let savedScrollY = 0;
let savedStyles: Partial<Record<typeof LOCKED_STYLES[number], string>> = {};

function lockBody() {
  lockCount++;
  if (lockCount > 1)
    return;

  const { style } = document.body;
  savedScrollX = window.scrollX;
  savedScrollY = window.scrollY;
  savedStyles = Object.fromEntries(LOCKED_STYLES.map(key => [key, style[key]]));

  style.position = 'fixed';
  style.top = `-${savedScrollY}px`;
  style.left = '0';
  style.right = '0';
  style.width = '100%';
  style.overflow = 'hidden';
  style.overscrollBehavior = 'none';
}

function unlockBody() {
  if (lockCount === 0)
    return;
  lockCount--;
  if (lockCount > 0)
    return;

  Object.assign(document.body.style, savedStyles);
  window.scrollTo({ left: savedScrollX, top: savedScrollY, behavior: 'instant' });
}

export function useBodyScrollLock() {
  let isLocked = false;

  function lock() {
    if (isLocked)
      return;
    isLocked = true;
    lockBody();
  }

  function unlock() {
    if (!isLocked)
      return;
    isLocked = false;
    unlockBody();
  }

  onScopeDispose(unlock);

  return { lock, unlock };
}
